import { Button, Modal, Stack, Text } from '@mantine/core'
import { IconRefresh } from '@tabler/icons-react'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useState } from 'react'

import { useGlobalState } from '@/hooks/useGlobalState'
import { getRoutePath } from '@/utils/navigation'
import { CHANNEL_SWITCH_STORAGE_KEY, load } from '@/utils/storage'

const CHECK_INTERVAL_MS = 60 * 1000 // 60s when tab is visible

export const ChannelChangeDetector = observer(function ChannelChangeDetector() {
  const { api, authenticationStore } = useGlobalState()
  const [showReloadModal, setShowReloadModal] = useState(false)

  const runCheck = useCallback(async () => {
    if (showReloadModal) return
    const currentSponsorId = authenticationStore.user?.sponsorId
    if (!currentSponsorId) return

    const fresh = await api.safeRequest(api.checkUser)
    if (!fresh.ok || !fresh.data?.sponsorId) return

    if (fresh.data.sponsorId !== currentSponsorId) {
      setShowReloadModal(true)
    }
  }, [api, authenticationStore.user?.sponsorId, showReloadModal])

  // Listen for channel switch in another tab (localStorage 'storage' event only fires in other tabs)
  useEffect(() => {
    if (!authenticationStore.isAuthenticated || !authenticationStore.ready) {
      return
    }

    const onStorage = (event: StorageEvent) => {
      if (event.key !== CHANNEL_SWITCH_STORAGE_KEY || event.newValue == null) return
      const payload = load<{ sponsorId: string }>(CHANNEL_SWITCH_STORAGE_KEY)
      const currentSponsorId = authenticationStore.user?.sponsorId
      if (payload?.sponsorId && currentSponsorId && payload.sponsorId !== currentSponsorId) {
        setShowReloadModal(true)
      }
    }

    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [authenticationStore.isAuthenticated, authenticationStore.ready, authenticationStore.user?.sponsorId])

  // Fallback: poll check_user when tab is visible (works when all tabs share the same API base URL)
  useEffect(() => {
    if (!authenticationStore.isAuthenticated || !authenticationStore.ready) {
      return
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void runCheck()
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    if (document.visibilityState === 'visible') {
      void runCheck()
    }

    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        void runCheck()
      }
    }, CHECK_INTERVAL_MS)

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      clearInterval(intervalId)
    }
  }, [authenticationStore.isAuthenticated, authenticationStore.ready, runCheck])

  const handleReload = () => {
    // If user is on a CM edit page, the module won't exist in the new channel — send them to dashboard
    const pathname = window.location.pathname
    const isEditingContentModule = pathname.includes('/edit')
    if (isEditingContentModule) {
      window.location.href = getRoutePath('dashboard')
    } else {
      window.location.reload()
    }
  }

  if (!showReloadModal) return null

  return (
    <Modal.Root opened onClose={() => undefined} centered closeOnClickOutside={false} closeOnEscape={false} size="md">
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Channel changed</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Stack gap="xl">
            <Text size="sm">
              The channel was changed in another tab. This page must be reloaded to show the correct channel. Please
              click below to reload.
            </Text>
            <Button onClick={handleReload} leftSection={<IconRefresh size={16} />}>
              Reload page
            </Button>
          </Stack>
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  )
})
